import { KTSVG } from '_metronic/helpers';
import NumericComponent from 'app/components/FormComponents/NumericComponent';
import SelectComponent from 'app/components/FormComponents/SelectComponent';
import TableComponent from 'app/components/TableComponent';
import * as Yup from 'yup';
import { getPerceptionDeductions } from 'app/service/catalog/Catalogs';
import { SelectOption } from 'app/types/commons';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import { PerceptionDeduction, Perception } from 'app/types/perceptions';
import { getCollaboratorPerceptions, postPerceptions, putPerceptions } from 'app/service/perception/perception';
import { Collaborator } from 'app/types/collaborator';

type Props = {
    collaborator?: Collaborator;
};

const heads = [
    {
        code: 'monthly',
        label: 'Salario mensual',
    },
    {
        code: 'daily',
        label: 'Salario diario',
    },
    {
        code: 'imssMonthly',
        label: 'Salario mensual IMSS',
    },
    {
        code: 'imssDaily',
        label: 'Salario diario IMSS',
    },
];

const prevHeads = [
    {
        label: 'Concepto',
        code: 'perception',
    },
    {
        label: 'Valor',
        code: 'value',
    },
    {
        label: 'Acciones',
        code: 'acciones',
    },
];

type PerceptionOption = SelectOption & {
    typeId?: number;
};

type FormValues = {
    typePerception: PerceptionOption | null;
    amount?: number;
};

const initialValues: FormValues = {
    typePerception: null,
    amount: 0,
};

const validationSchema = Yup.object({
    typePerception: Yup.object({
        value: Yup.number().min(1, "La cantidad tiene que ser mayor que 0"),
        label: Yup.string(),
    })
        .nullable()
        .required('Este campo es requerido'),
    amount: Yup.number()
        .when('typePerception', (typePerception, schema) => {
            if ([3, 4].includes(Number(typePerception?.typeId))) {
                return schema
                    .required('Este campo es requerido')
                    .min(1, "La cantidad tiene que ser mayor que 0")
                    .max(999999, 'El valor total no puede ser mayor a 999,999');
            }
            return schema.notRequired().nullable();
        })
});

const Perceptions: React.FC<Props> = ({ collaborator }) => {
    const [show, setShow] = useState(false);
    const [typesPerception, setTypesPerception] = useState<PerceptionDeduction[]>([]);
    const [selectPerception, setSelectPerception] = useState<PerceptionOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<any[]>([]);
    const [prevRows, setPrevRows] = useState<any[]>([]);
    const formData = useFormik<FormValues>({
        initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setLoading(true);
            try {
                await postPerceptions({
                    collaboratorId: Number(collaborator!.id),
                    perceptionId: values.typePerception?.value,
                    value: Number(value),
                })
                formData.resetForm();
                fillDataAsync();
                handleClose();
                const message = 'Se actualizó la información de percepciones con éxito';
                toast.success(message, {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: 'light',
                });
            } catch (error: any) {
                console.error('error en submit', error)
                const defaultError = 'Hubo un error al intentar actualizar los datos de percepciones';
                toast.error(error.message ?? defaultError, {
                    position: 'top-right',
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: 'light',
                });
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });
    const typePerception = useMemo(() => {
        if (formData.values.typePerception?.value && typesPerception) {
            return typesPerception.find((item) => item.id === formData.values.typePerception?.value);
        }
        return null;
    }, [formData.values.typePerception, typesPerception]);
    const value = useMemo(() => {
        if (typePerception) {
            switch (typePerception.typePerceptionDeductionId) {
                case 1:
                    return typePerception.value;
                case 2:
                    switch (typePerception.factorBaseId) {
                        case 1:
                            return typePerception.value * Number(collaborator!.employe?.baseSalary);
                        case 2:
                            return typePerception.value * Number(collaborator!.employe?.dayliSalary);
                        case 3:
                            return typePerception.value * Number(collaborator!.employe?.baseSalary) / collaborator!.employe?.factor[0].value!;
                        default:
                            return typePerception.value * Number(collaborator!.employe?.baseSalary);
                    }
                case 3:
                    return (formData.values.amount ?? 0) * Number(typePerception.value);
                case 4:
                default:
                    return (Number(formData.values.amount ?? 0));
            }
        }
        return 0;
    }, [typePerception, formData.values.amount]);
    const handleClose = () => {
        formData.resetForm();
        setShow(false);
    };
    const addRow = () => {
        setShow(true);
    };
    const handleDeletePerception = async (item: Perception) => {
        try {
            await putPerceptions(item.id!, {
                id: item.id!,
                perceptionId: item.perceptionId,
                collaboratorId: item.collaboratorId,
                value: item.value,
                statusId: 2
            });
            toast.success('Se eliminó la percepción con éxito');
            fillDataAsync();
        } catch (error: any) {
            console.error('error deleting perception', error);
            toast.error(error.message ?? 'Hubo un error al intentar eliminar la percepción');
        }
    };
    const fillDataAsync = async () => {
        try {
            const typesRes = await getPerceptionDeductions();
            const prevRes = await getCollaboratorPerceptions(collaborator!.id!)
                .catch((error) => {
                    console.error('error al cargar las percepciones anteriores', error);
                });
            if (prevRes) setPrevRows(prevRes.filter((item) => item.statusId !== 2).map((item) => ({
                perception: item.perception,
                value: item.value.toLocaleString(),
                acciones: (
                    <div
                        style={{
                            textAlign: 'center',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleDeletePerception(item)}
                    >
                        <KTSVG
                            path="/media/icons/duotune/general/gen027.svg"
                            className="svg-icon-3 svg-icon-danger"
                        />
                    </div>
                )
            })));
            setTypesPerception(typesRes.records);
            setSelectPerception(typesRes.records
                .filter((item) => item.typeMovementId === 1)
                .map((item) => ({
                    value: item.id,
                    label: item.description,
                    typeId: item.typePerceptionDeductionId,
                })));
        } catch (error) {
            console.error('error en carga de catalogos', error)
        }
    };
    useEffect(() => {
        if (collaborator) {
            fillDataAsync();
            if (collaborator.employe) {
                setRows([{
                    monthly: collaborator.employe?.baseSalary.toFixed(2).toLocaleString(),
                    daily: (Number(collaborator.employe?.baseSalary)! / collaborator.employe?.factor[0].value!).toFixed(2).toLocaleString(),
                    imssMonthly: (Number(collaborator.employe?.dayliSalary)! * collaborator.employe?.factor[0].value!).toFixed(2).toLocaleString(),
                    imssDaily: collaborator.employe?.dayliSalary.toFixed(2).toLocaleString(),
                }]);
            }
        }
    }, [collaborator]);
    return (
        <div>
            <TableComponent heads={heads} rows={rows} />
            <div>
                <h2>Frecuencia de pago:</h2>
                <div className='d-flex align-items-center gap-2'>
                    <div className='w-10 me-2'>
                        <input
                            className='form-control'
                            type='text'
                            value="Quincenal"
                            disabled
                        />
                    </div>
                    <h1>15</h1>
                    <h3>días</h3>
                </div>
            </div>
            <div className='w-100 d-flex justify-content-center'>
                <div className='w-50'>
                    <TableComponent heads={prevHeads} rows={prevRows} />
                </div>
            </div>
            <div className="d-flex align-items-center mt-4">
                <button
                    className="btn btn-light-primary btn-sm mb-10"
                    onClick={addRow}
                    disabled={!collaborator?.employe}
                >
                    <KTSVG
                        path="/media/icons/duotune/arrows/arr075.svg"
                        className="svg-icon-5"
                    />
                    Agregar Percepción
                </button>
            </div>
            <Modal
                id="kt_modal_create"
                tabIndex={-1}
                aria-hidden="true"
                dialogClassName="modal-dialog"
                show={show}
                onHide={handleClose}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h2 className="text-muted">Percepción</h2>
                        <div
                            className="btn btn-sm btn-icon btn-active-color-primary"
                            onClick={handleClose}
                        >
                            <KTSVG
                                className="svg-icon-2"
                                path="/media/icons/duotune/arrows/arr061.svg"
                            />
                        </div>
                    </div>
                    <div className="modal-body mx-5 padding-top-00">
                        <form
                            id="kt_modal_generic_form"
                            className="form"
                            action="#"
                            onSubmit={formData.handleSubmit}
                        >
                            <div className='m-3'>
                                <SelectComponent
                                    options={selectPerception}
                                    formData={formData}
                                    name='typePerception'
                                    display={'-1'}
                                />
                                {formData.touched.typePerception && formData.errors.typePerception && (
                                    <div className="fv-plugins-message-container text-danger w-100">
                                        <span role="alert">{formData.errors.typePerception}</span>
                                    </div>
                                )}
                            </div>
                            <div className="d-flex flex-column flex-xl-row flex-row-fluid mt-5">
                                <div className="w-100 d-flex">
                                    <div className="w-100 pe-5">
                                        {(typeof typePerception?.typePerceptionDeductionId === 'number') && (<div className='container'>
                                            <div className='row'>
                                                {([1, 2, 3].includes(typePerception?.typePerceptionDeductionId))
                                                    && (
                                                        <div className='col'>
                                                            <h4>Valor</h4>
                                                            <p>{typePerception?.typePerceptionDeductionId === 2 ? `${typePerception.value * 100}%` : typePerception.value}</p>
                                                        </div>
                                                    )}
                                                {([3, 4].includes(typePerception?.typePerceptionDeductionId)) &&
                                                    (
                                                        <div className='col'>
                                                            <NumericComponent
                                                                label='Cantidad'
                                                                formData={formData}
                                                                name="amount"
                                                                display={'-1'}
                                                            />
                                                            {formData.touched.amount && formData.errors.amount && (
                                                                <div className="fv-plugins-message-container text-danger w-100">
                                                                    <span role="alert">{formData.errors.amount}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                            </div>
                                            <div className='row'>
                                                <div className='col mt-5'>
                                                    <h4>Valor Total</h4>
                                                    <p>{typeof value === 'number' ? value.toFixed(2) : '0.00'}</p>
                                                </div>
                                            </div>
                                        </div>)}
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end mt-10">
                                <button
                                    type="reset"
                                    className="btn btn-light me-3"
                                    data-kt-users-modal-action="cancel"
                                    onClick={handleClose}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    data-kt-users-modal-action="submit"
                                >
                                    {!loading && <span className="indicator-label">Guardar</span>}
                                    {loading && (
                                        <span
                                            className="indicator-progress"
                                            style={{ display: "block" }}
                                        >
                                            Espere por favor...
                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Perceptions;